import { useCallback, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { H5 } from 'components/Text/H5';
import { configValue } from 'config/appConfigUtils';
import { useViewportSizeEffect } from 'hooks/viewport/useViewportSizeEffect';
import { gutterWidth, transition } from 'theme/theme';

import { flashMessageTypeMap } from './flashMessageTypeMap';
import { styleColor } from './styleColor';

type Props = {
  flashMessage: FlashMessageType;
  removeMessage: () => void;
};

const StyledButton = styled(Button)<{
  $color: string;
}>`
  &.medium {
    color: ${(props) => props.$color};
  }
`;

function FlashMessage({ flashMessage, removeMessage, ...props }: Props) {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const { message, style } = flashMessageTypeMap[flashMessage.type](
    flashMessage.data,
  );
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const flashTheme = styleColor[style] || styleColor.info;

  const closeHandler = () => {
    removeMessage();

    if (flashMessage.onRemove) {
      flashMessage.onRemove();
    }
  };

  return (
    <Box
      data-qa-id="flash-message"
      display="flex"
      position="relative"
      alignItems="center"
      justifyContent="center"
      px={gutterWidth / 2 + 8}
      py="10px"
      bg={flashTheme.bg}
      color={flashTheme.color}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <H5 my={0} px={[gutterWidth / 2, null, null, 0]}>
        {message}
      </H5>
      <Box
        position="absolute"
        right={gutterWidth / 2}
        top="50%"
        transform="translateY(-50%)"
      >
        <StyledButton
          data-qa-id="flash-message-close"
          onClick={closeHandler}
          aria-label={getText('Close alert message')}
          icon={{ type: 'normalized', name: 'close' }}
          variant="tertiary-grey"
          $color={flashTheme.color}
        />
      </Box>
    </Box>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const aDisappear = (height: any) => keyframes`
  from {
    opacity: 1;
    ${
      height !== 'auto' &&
      height > 0 &&
      `
      height: ${height}px;
    `
    }
    pointer-events: auto;
  }
  to {
    opacity: 0;
    height: 0;
    pointer-events: none;
  }
`;

const DisappearingContainer = styled.div<{
  $height?: string | number;
  $transitionTime?: string;
}>`
  ${(props) =>
    props.$height !== 'auto' &&
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    props.height > 0 &&
    `
  overflow: hidden;
  height: ${props.$height}px;
`}
  animation: ${transition} ${(props) =>
    `${props.$transitionTime || 5}`}s forwards ${aDisappear};
  will-change: height;
`;

export function DisappearingFlashMessage(props: Props) {
  const [height, setHeight] = useState('auto');
  const containerRef = useRef(null);
  const transitionTime = configValue('idealist', 'flashMessageDuration');

  useViewportSizeEffect(
    useCallback(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (containerRef && containerRef.current) {
        // @ts-expect-error TS(2339): Property 'offsetHeight' does not exist on type 'ne... Remove this comment to see the full error message
        const newHeight = containerRef.current.offsetHeight;
        setHeight(newHeight || 'auto');
      }
    }, []),
  );

  return (
    <DisappearingContainer
      ref={containerRef}
      $height={height}
      // @ts-expect-error TS(2769): No overload matches this call.
      $transitionTime={transitionTime}
    >
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FlashMessage {...props} />
    </DisappearingContainer>
  );
}
